/**
 * The face of the instrument.
 *
 * The tile *renders*. It holds no engine reference, decides nothing about what a touch
 * means, and never asks a question about the project it was not handed — the board hands it
 * a finished `PadState` and the tile draws it. That separation is what keeps a trigger from
 * ever waiting on a view update.
 *
 * Three rules are load-bearing here:
 *
 * 1. **The engine call is the first statement in the pointerdown handler.** Nothing above
 *    it allocates, reads layout, or touches a signal. The visual press state is applied on
 *    the line *after*, in the same synchronous handler, so the finger gets its answer in
 *    the same task without the sound waiting for a paint.
 * 2. **Real DOM, no canvas.** A pad is a rectangle with three lines of text; drawing that
 *    on a canvas would cost us text rendering, VoiceOver, Dynamic Type and selection
 *    highlighting in exchange for nothing. The only canvas on a tile is the 2 px level
 *    hairline, which genuinely is a picture.
 * 3. **Everything is said twice.** Colour is always paired with a glyph, a kicker word, or
 *    a shape that changes. A user who cannot separate magenta from grey still reads
 *    "PLAYING" and sees the bottom rule fill.
 */

import { h, on, onCleanup, schedule } from '../../core/dom';
import {
  clamp,
  padColorOnTint,
  padColorTint,
  padIsEmpty,
  padRestingLabel,
  padStateAccessibilityDescription,
  padStateGlyph,
  padStateIsSounding,
  playbackModeDisplayName,
} from '../../model/types';
import type { Pad, PadState } from '../../model/types';
import { bindReactive, capturePointer, hasIcon, icon, nextId } from './controls';
import type { Reactive } from './controls';
import { padMeter } from './meters';
import type { PadMeterHandle } from './meters';

// MARK: - Trigger contracts

export interface PadTriggerContext {
  /** -1 for a keyboard activation, so the board can tell one from a finger. */
  readonly pointerId: number;
  readonly pointerType: string;
  /**
   * 0…1. Pointer Events report 0.5 for a device with no force sensor, which is
   * indistinguishable from a genuine half-press, so that reading is treated as a full
   * velocity hit. Force Touch and Apple Pencil report a real curve and it is passed through.
   */
  readonly velocity: number;
  /** `event.timeStamp` — the same origin `performance.now()` uses, for latency measurement. */
  readonly timeStamp: number;
}

export interface PadReleaseContext extends PadTriggerContext {
  /** Milliseconds the pad was held. */
  readonly duration: number;
  /** Short press that never travelled — the gesture that means "open me" on an empty pad. */
  readonly wasTap: boolean;
  readonly longPressed: boolean;
}

export interface PadTileCallbacks {
  /**
   * Called synchronously from `pointerdown`, before any view state changes. Play the sound
   * here and nowhere else. This fires for empty pads too — the tile does not know what an
   * empty pad means, and a board that opens a picker should do it from `onPadUp` so the
   * gesture can still be cancelled by sliding off.
   */
  onPadDown(pad: Pad, context: PadTriggerContext): void;
  onPadUp(pad: Pad, context: PadReleaseContext): void;
  /** The system took the pointer — a call arrived, or the browser started a system gesture. */
  onPadCancel?(pad: Pad, context: PadTriggerContext): void;
  /** Held past the threshold without moving: open the pad editor. */
  onPadLongPress?(pad: Pad): void;
}

// MARK: - Options

export interface PadTileOptions {
  readonly pad: Pad;
  /** Where the tile sits in its bank, 0-based, and how many there are. */
  readonly position?: { readonly index: number; readonly total: number };
  readonly state?: Reactive<PadState>;
  /** 0…1 through the sounding region. Drives the rule along the bottom edge. */
  readonly progress?: Reactive<number>;
  /** Linear 0…1 output level for this pad. */
  readonly level?: Reactive<number>;
  /** Set on dense grids (4×6) where the label has to give up a couple of points. */
  readonly compact?: boolean;
  readonly showsMeter?: boolean;
  /**
   * Resolves the pad's artwork to a URL. Injected rather than imported so the tile keeps no
   * dependency on the storage layer and stays usable in isolation.
   */
  readonly artworkURL?: (pad: Pad) => Promise<string | null>;
  readonly callbacks: PadTileCallbacks;
}

export interface PadTileHandle {
  readonly el: HTMLElement;
  readonly padId: string;
  setPad(pad: Pad): void;
  setState(state: PadState): void;
  setProgress(progress: number): void;
  setLevel(level: number): void;
  setCompact(compact: boolean): void;
  /** Where this tile sits in the bank, 0-based. Folded into the accessible name. */
  setPosition(index: number, total: number): void;
  /** Extra sentence appended to the spoken description, e.g. the board's edit mode. */
  setSpokenNote(note: string): void;
  destroy(): void;
}

// MARK: - Multi-touch bookkeeping
//
// A board is played with several fingers at once, so the tile can never assume it owns "the"
// pointer. Each tile keeps its own set of live pointer ids, and this module-level map is the
// board-wide view of which finger is on which pad — used to release capture cleanly when a
// tile is torn down mid-gesture, and readable by the latency harness.

const pointerToPad = new Map<number, string>();

/** Live pointer id → pad id, for diagnostics and the latency harness. */
export function activePadPointers(): ReadonlyMap<number, string> {
  return pointerToPad;
}

export function heldPadCount(): number {
  return pointerToPad.size;
}

const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP = 12;
const TAP_SLOP = 8;
const TAP_MS = 260;

// MARK: - Derived presentation

interface StatusKicker {
  readonly text: string;
  readonly tone: 'tint' | 'acid' | 'record' | 'secondary' | 'warning' | 'danger';
}

/**
 * The coloured word under the label. It is what keeps a magenta tile from being a hue-only
 * signal, and it is the line the reference board puts under a playing sound.
 *
 * Muted and soloed are absent on purpose: their corner glyphs already say it, and a third
 * line on a small tile costs more than it explains.
 */
function statusKicker(state: PadState): StatusKicker | null {
  switch (state) {
    case 'playing':
      return { text: 'Playing', tone: 'tint' };
    case 'looping':
      return { text: 'Looping', tone: 'tint' };
    case 'queued':
      return { text: 'Queued', tone: 'acid' };
    case 'recording':
      return { text: 'Recording', tone: 'record' };
    case 'processing':
      return { text: 'Working', tone: 'secondary' };
    case 'missingSource':
      return { text: 'Missing file', tone: 'warning' };
    case 'decodeFailed':
      return { text: "Can't play", tone: 'danger' };
    case 'empty':
    case 'loaded':
    case 'pressed':
    case 'muted':
    case 'soloed':
      return null;
  }
}

/**
 * The dim line above the label — the pad's own metadata. This is the "PLAYS OVER" detail
 * from the reference board: how the pad behaves when it is hit, which is the one thing about
 * a pad you cannot work out by looking at it.
 */
function metadataKicker(pad: Pad): string | null {
  if (pad.category !== undefined && pad.category !== '') return pad.category;
  return pad.playbackMode === 'oneShot' ? null : playbackModeDisplayName(pad.playbackMode);
}

/**
 * Only states that persist get a corner badge. Sounding states are already carried by the
 * wash, the rule and the kicker; a badge on top of those would make the busiest moment on
 * the board the noisiest.
 */
function badgeGlyph(state: PadState): string | null {
  switch (state) {
    case 'muted':
    case 'soloed':
    case 'processing':
    case 'missingSource':
    case 'decodeFailed':
      return padStateGlyph(state);
    default:
      return null;
  }
}

function badgeTone(state: PadState): string {
  switch (state) {
    case 'soloed':
      return 'acid';
    case 'missingSource':
      return 'warning';
    case 'decodeFailed':
      return 'danger';
    default:
      return 'secondary';
  }
}

// MARK: - The tile

export function padTile(options: PadTileOptions): PadTileHandle {
  let pad = options.pad;
  let state: PadState = 'loaded';
  let compact = options.compact === true;
  let position = options.position ?? null;
  let spokenNote = '';
  const showsMeter = options.showsMeter !== false;

  const statusId = `pad-state-${nextId()}`;

  const kickerLine = h('span', { class: 'kicker pad__kicker' });
  const labelLine = h('span', { class: 'pad__label' });
  const statusLine = h('span', { class: 'kicker pad__status' });
  const emptyGlyph = h('span', { class: 'pad__empty', 'aria-hidden': 'true' }, icon('plus', { size: compact ? 15 : 18 }));
  const badge = h('span', { class: 'pad__badge', 'aria-hidden': 'true' });
  const progressFill = h('i', { class: 'pad__progress-fill' });
  const progressRule = h(
    'div',
    { class: 'pad__progress', 'aria-hidden': 'true' },
    progressFill,
    h('i', { class: 'pad__progress-glint' }),
  );
  const spokenState = h('span', { class: 'visually-hidden', id: statusId });

  const meter: PadMeterHandle | null = showsMeter
    ? padMeter({ tint: padColorTint(pad.color), class: 'pad__meter' })
    : null;

  /** The pad's chosen SF Symbol, when it has one. Sits above the label, never replacing it. */
  const symbolHost = h('span', { class: 'pad__symbol', 'aria-hidden': 'true' });
  /** The picture a pad wears, behind everything and dimmed so the label stays readable. */
  const artwork = h('span', { class: 'pad__artwork', 'aria-hidden': 'true' });

  const body = h('span', { class: 'pad__body' }, symbolHost, kickerLine, labelLine, statusLine);

  const el = h(
    'div',
    {
      class: 'pad instrument',
      role: 'button',
      tabindex: '0',
      draggable: 'false',
      'aria-describedby': statusId,
      'aria-pressed': 'false',
      dataset: { padId: pad.id, state: 'loaded' },
    },
    artwork,
    h('span', { class: 'pad__wash', 'aria-hidden': 'true' }),
    body,
    emptyGlyph,
    meter === null ? null : meter.el,
    badge,
    progressRule,
    spokenState,
  );

  // --- Rendering -------------------------------------------------------------------

  /**
   * A board is a spatial instrument, and on a template where half the slots are empty the
   * old name — a bare `"Empty pad"` on every one of them — gave a screen-reader user no way
   * to tell which square they were on or which one they had just filled. The position is in
   * the name rather than the description so it is heard on the first swipe, and it trails the
   * pad's own name when there is one, because the name is what the user is looking for.
   */
  function accessibleName(): string {
    const empty = padIsEmpty(pad);
    const label = padRestingLabel(pad);
    const where = position === null ? '' : `pad ${position.index + 1} of ${position.total}`;
    if (empty) return where === '' ? 'Empty pad' : `Empty ${where}`;
    const name = label === '' ? 'Untitled pad' : label;
    return where === '' ? name : `${name}, ${where}`;
  }

  function renderPad(): void {
    const empty = padIsEmpty(pad);
    el.dataset['padId'] = pad.id;
    el.style.setProperty('--pad-tint', padColorTint(pad.color));
    el.style.setProperty('--pad-on-tint', padColorOnTint(pad.color));
    el.classList.toggle('is-empty', empty);
    meter?.setTint(padColorTint(pad.color));

    labelLine.textContent = padRestingLabel(pad);
    // The icon and the artwork are both decoration for the name, never a replacement: the
    // tile always says what the pad is in words as well.
    symbolHost.replaceChildren();
    if (!empty && pad.symbolName !== undefined && hasIcon(pad.symbolName)) {
      symbolHost.appendChild(icon(pad.symbolName, { size: compact ? 13 : 16 }));
    }
    el.classList.toggle('has-symbol', symbolHost.childElementCount > 0);
    applyArtwork();
    el.setAttribute('aria-label', accessibleName());
    // The pad's own category, when it has one, is part of what the pad *is* — a sighted
    // player reads it off the tile's kicker, so it belongs in the spoken description too.
    if (pad.category !== undefined && pad.category !== '') el.setAttribute('aria-roledescription', `${pad.category} pad`);
    else el.removeAttribute('aria-roledescription');
    if (position !== null) {
      el.setAttribute('aria-posinset', String(position.index + 1));
      el.setAttribute('aria-setsize', String(position.total));
    }
    renderState();
  }

  /** Loads the pad's picture, if any, and paints it as the tile's ground. */
  function applyArtwork(): void {
    const wanted = pad.artworkFileName;
    if (wanted === undefined) {
      artwork.style.removeProperty('background-image');
      el.classList.remove('has-artwork');
      artworkToken = null;
      return;
    }
    if (artworkToken === wanted) return;
    artworkToken = wanted;
    void options.artworkURL?.(pad).then((url) => {
      // A second edit may have landed while the blob was being read.
      if (artworkToken !== wanted) return;
      if (url === null) {
        // The picture could not be read. Show nothing rather than the one it replaced, and
        // clear the token so a later render tries again instead of latching this pad blank.
        artwork.style.removeProperty('background-image');
        el.classList.remove('has-artwork');
        artworkToken = null;
        return;
      }
      artwork.style.backgroundImage = `url("${url}")`;
      el.classList.add('has-artwork');
    });
  }
  let artworkToken: string | null = null;

  function renderState(): void {
    const empty = padIsEmpty(pad) || state === 'empty';
    const status = statusKicker(state);
    const metadata = metadataKicker(pad);
    // A dense grid cannot carry three lines of text. When something is happening, what is
    // happening wins over how the pad is configured.
    const showsMetadata = metadata !== null && !(compact && status !== null);

    el.dataset['state'] = state;
    el.classList.toggle('is-empty', empty);
    el.classList.toggle('is-sounding', padStateIsSounding(state));
    el.classList.toggle('is-looping', state === 'looping');
    el.classList.toggle('is-queued', state === 'queued');
    el.classList.toggle('is-recording', state === 'recording');
    el.classList.toggle('is-muted', state === 'muted');

    kickerLine.textContent = showsMetadata && metadata !== null ? metadata : '';
    kickerLine.hidden = !showsMetadata || metadata === null;

    statusLine.textContent = status?.text ?? '';
    statusLine.hidden = status === null;
    if (status !== null) statusLine.dataset['tone'] = status.tone;

    // The recording badge is a pulsing bloom rather than a glyph: it is the one state where
    // "still going" matters more than "which state", and a pulse reads at arm's length. The
    // bloom is a filled disc, never a ring — an accent belongs to a fill in this app, and the
    // solid dot paints on top of it, so it is written first.
    if (state === 'recording') {
      badge.replaceChildren(
        h('i', { class: 'pad__rec-bloom' }),
        h('i', { class: 'pad__rec-dot' }),
      );
      badge.hidden = false;
      badge.dataset['tone'] = 'record';
    } else {
      const glyph = badgeGlyph(state);
      if (glyph === null) {
        badge.replaceChildren();
        badge.hidden = true;
      } else {
        badge.replaceChildren(icon(glyph, { size: 11 }));
        badge.hidden = false;
        badge.dataset['tone'] = badgeTone(state);
      }
    }

    const described = padStateAccessibilityDescription(state);
    spokenState.textContent = spokenNote === '' ? described : `${described} ${spokenNote}`;
    el.setAttribute('aria-pressed', held.size > 0 || padStateIsSounding(state) ? 'true' : 'false');
  }

  let pendingProgress: number | null = null;
  let cancelProgressWrite: (() => void) | null = null;

  /**
   * The bottom rule is a scaled element, never a width. `transform` and `opacity` are the
   * only two properties the compositor can animate without the main thread, so a loop that
   * wraps sixty times a minute never enters layout — which is the whole reason the progress
   * rule can be trusted during a performance.
   */
  function writeProgress(): void {
    cancelProgressWrite = null;
    if (pendingProgress === null) return;
    progressFill.style.setProperty('transform', `scaleX(${pendingProgress})`);
    pendingProgress = null;
  }

  function setProgress(progress: number): void {
    const clamped = clamp(Number.isFinite(progress) ? progress : 0, 0, 1);
    pendingProgress = clamped;
    if (cancelProgressWrite === null) cancelProgressWrite = schedule(writeProgress);
  }

  function setCompact(next: boolean): void {
    if (next === compact) return;
    compact = next;
    el.classList.toggle('is-compact', compact);
    emptyGlyph.replaceChildren(icon('plus', { size: compact ? 15 : 18 }));
    renderState();
  }

  // --- Pointer handling ------------------------------------------------------------
  //
  // `setPointerCapture` on pointerdown is what makes a held pad survive a finger that
  // drifts off its edge: the up event still arrives here, so a gate pad releases when the
  // finger lifts rather than sticking on for ever. It is also what lets six fingers hold
  // six pads without any of them stealing another's events.

  interface HeldPointer {
    readonly startX: number;
    readonly startY: number;
    readonly startedAt: number;
    moved: number;
    longPressed: boolean;
    longPressTimer: number;
  }

  const held = new Map<number, HeldPointer>();

  function contextFor(event: PointerEvent): PadTriggerContext {
    const pressure = event.pressure;
    return {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      velocity: pressure > 0 && pressure !== 0.5 ? pressure : 1,
      timeStamp: event.timeStamp,
    };
  }

  function clearHold(pointerId: number): HeldPointer | undefined {
    const entry = held.get(pointerId);
    if (entry === undefined) return undefined;
    if (entry.longPressTimer !== 0) clearTimeout(entry.longPressTimer);
    held.delete(pointerId);
    pointerToPad.delete(pointerId);
    if (held.size === 0) el.classList.remove('is-held');
    el.setAttribute('aria-pressed', held.size > 0 || padStateIsSounding(state) ? 'true' : 'false');
    return entry;
  }

  onCleanup(
    el,
    on(el, 'pointerdown', ((event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (held.has(event.pointerId)) return;

      // ---- The audio path. Nothing may be inserted above this line. ----
      options.callbacks.onPadDown(pad, contextFor(event));
      // ---- Everything below is presentation. ----

      el.classList.add('is-held');
      el.setAttribute('aria-pressed', 'true');

      const entry: HeldPointer = {
        startX: event.clientX,
        startY: event.clientY,
        startedAt: event.timeStamp,
        moved: 0,
        longPressed: false,
        longPressTimer: 0,
      };
      held.set(event.pointerId, entry);
      pointerToPad.set(event.pointerId, pad.id);
      capturePointer(el, event.pointerId);

      if (options.callbacks.onPadLongPress !== undefined) {
        entry.longPressTimer = self.setTimeout(() => {
          entry.longPressTimer = 0;
          if (!held.has(event.pointerId) || entry.moved > LONG_PRESS_SLOP) return;
          entry.longPressed = true;
          el.classList.add('is-longpressed');
          options.callbacks.onPadLongPress?.(pad);
        }, LONG_PRESS_MS);
      }

      // After the engine call: stops the iOS text-selection loupe and any residual
      // synthesised mouse event, neither of which the instrument surface wants.
      event.preventDefault();
    }) as EventListener, { passive: false }),
  );

  onCleanup(
    el,
    on(el, 'pointermove', ((event: PointerEvent) => {
      const entry = held.get(event.pointerId);
      if (entry === undefined) return;
      entry.moved = Math.max(
        entry.moved,
        Math.hypot(event.clientX - entry.startX, event.clientY - entry.startY),
      );
    }) as EventListener),
  );

  onCleanup(
    el,
    on(el, 'pointerup', ((event: PointerEvent) => {
      const entry = clearHold(event.pointerId);
      if (entry === undefined) return;
      el.classList.remove('is-longpressed');
      const duration = event.timeStamp - entry.startedAt;
      options.callbacks.onPadUp(pad, {
        ...contextFor(event),
        duration,
        wasTap: entry.moved <= TAP_SLOP && duration <= TAP_MS,
        longPressed: entry.longPressed,
      });
    }) as EventListener),
  );

  const abandon = ((event: PointerEvent) => {
    const entry = clearHold(event.pointerId);
    if (entry === undefined) return;
    el.classList.remove('is-longpressed');
    options.callbacks.onPadCancel?.(pad, contextFor(event));
  }) as EventListener;

  onCleanup(el, on(el, 'pointercancel', abandon));
  // Capture can be taken away by the browser (a system edge gesture, a page navigation).
  // Without this the tile would keep a phantom finger and the voice would never release.
  onCleanup(el, on(el, 'lostpointercapture', ((event: PointerEvent) => {
    if (!held.has(event.pointerId)) return;
    abandon(event);
  }) as EventListener));

  onCleanup(el, on(el, 'dragstart', ((event: Event) => event.preventDefault()) as EventListener));
  onCleanup(el, on(el, 'contextmenu', ((event: Event) => event.preventDefault()) as EventListener));

  // --- Keyboard --------------------------------------------------------------------

  let keyboardHeld = false;

  onCleanup(
    el,
    on(el, 'keydown', ((event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      // The window-level shortcut handler also claims Space for the transport. Without this
      // line a keyboard user who tabbed to a pad and pressed Space fired the pad *and*
      // started the whole pattern — something no pointer gesture can do, and something no
      // user asked for.
      event.stopPropagation();
      if (event.repeat || keyboardHeld) return;
      keyboardHeld = true;
      options.callbacks.onPadDown(pad, {
        pointerId: -1,
        pointerType: 'keyboard',
        velocity: 1,
        timeStamp: event.timeStamp,
      });
      el.classList.add('is-held');
      el.setAttribute('aria-pressed', 'true');
    }) as EventListener),
  );

  const endKeyboard = (timeStamp: number): void => {
    if (!keyboardHeld) return;
    keyboardHeld = false;
    if (held.size === 0) el.classList.remove('is-held');
    el.setAttribute('aria-pressed', padStateIsSounding(state) ? 'true' : 'false');
    options.callbacks.onPadUp(pad, {
      pointerId: -1,
      pointerType: 'keyboard',
      velocity: 1,
      timeStamp,
      duration: 0,
      wasTap: true,
      longPressed: false,
    });
  };

  onCleanup(
    el,
    on(el, 'keyup', ((event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      event.stopPropagation();
      endKeyboard(event.timeStamp);
    }) as EventListener),
  );
  // A pad held with the keyboard when focus leaves would sustain for ever otherwise.
  onCleanup(el, on(el, 'blur', (() => endKeyboard(performance.now())) as EventListener));

  /**
   * The synthetic-activation fallback.
   *
   * Every other control in the system activates on `click`; the pad listened only to pointer
   * and key sequences. Anything that dispatches a bare click — Full Keyboard Access, some
   * switch-control setups, `element.click()` in a test — therefore reached the tile and got
   * silence. This fires only when no real sequence is in flight, so a finger or a key never
   * double-triggers through it.
   */
  onCleanup(
    el,
    on(el, 'click', ((event: MouseEvent) => {
      if (held.size > 0 || keyboardHeld) return;
      // A click synthesised from a pointer sequence carries a real coordinate; a
      // programmatic or assistive one reports 0/0 with no pointer detail.
      if (event.detail > 0) return;
      const context: PadTriggerContext = { pointerId: -1, pointerType: 'synthetic', velocity: 1, timeStamp: event.timeStamp };
      options.callbacks.onPadDown(pad, context);
      options.callbacks.onPadUp(pad, { ...context, duration: 0, wasTap: true, longPressed: false });
    }) as EventListener),
  );

  // --- Reactive wiring -------------------------------------------------------------

  if (options.state !== undefined) {
    bindReactive(el, options.state, (next) => {
      if (next === state) return;
      state = next;
      renderState();
    });
  }
  if (options.progress !== undefined) bindReactive(el, options.progress, setProgress);
  if (options.level !== undefined && meter !== null) {
    bindReactive(el, options.level, (value) => meter.setLevel(value));
  }

  onCleanup(el, () => {
    for (const pointerId of [...held.keys()]) clearHold(pointerId);
    cancelProgressWrite?.();
    meter?.destroy();
  });

  if (compact) el.classList.add('is-compact');
  renderPad();
  setProgress(0);
  writeProgress();

  return {
    el,
    get padId() {
      return pad.id;
    },
    setPad(next: Pad): void {
      pad = next;
      renderPad();
    },
    setState(next: PadState): void {
      if (next === state) return;
      state = next;
      renderState();
    },
    setProgress,
    setLevel(level: number): void {
      meter?.setLevel(level);
    },
    setCompact,
    setPosition(index: number, total: number): void {
      if (position !== null && position.index === index && position.total === total) return;
      position = { index, total };
      el.setAttribute('aria-label', accessibleName());
      el.setAttribute('aria-posinset', String(index + 1));
      el.setAttribute('aria-setsize', String(total));
    },
    setSpokenNote(note: string): void {
      if (note === spokenNote) return;
      spokenNote = note;
      renderState();
    },
    destroy(): void {
      for (const pointerId of [...held.keys()]) clearHold(pointerId);
      cancelProgressWrite?.();
      meter?.destroy();
      el.remove();
    },
  };
}

// MARK: - Handle lookup
//
// `list()` reconciles by key and hands the update callback a bare `Element`, so the board
// needs a way back from the element to the tile that owns it. A WeakMap keeps that lookup
// without giving the DOM node a property the rest of the app could reach into.

const handles = new WeakMap<Element, PadTileHandle>();

/** Builds a tile and registers it so `padTileFor` can find it during a keyed list update. */
export function createPadTile(options: PadTileOptions): PadTileHandle {
  const handle = padTile(options);
  handles.set(handle.el, handle);
  return handle;
}

export function padTileFor(element: Element): PadTileHandle | undefined {
  return handles.get(element);
}
